import React, { useState } from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import { FormattedMessage, useIntl } from "react-intl"
import { Box, Button, Checkbox, FormControlLabel, Link, Typography } from "@carrotfertility/carotene-core"
import { Link as RouterLink } from "react-router-dom"
import { DbgPaths } from "#/utils/Paths"

export function BeforeYouContinueStep(): JSX.Element {
  const intl = useIntl()
  const { send } = useStateMachine()

  const [acknowledgements, setAcknowledgements] = useState({
    oneTransactionAtATime: false,
    checkedBenefitGuide: false,
    uploadDocuments: false,
    subjectToTaxes: false
  })

  return (
    <Box>
      <Box sx={(theme) => ({ display: "flex", justifyContent: "center", marginBlockEnd: theme.tokens.spacing.md })}>
        <Box
          component="img"
          src="/images/exclamation-mark-sign.png"
          srcSet="/images/exclamation-mark-sign-2x.png"
          alt=""
          sx={{ blockSize: "250px", inlineSize: "250px" }}
        />
      </Box>
      <Typography variant="h3" sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.xs })}>
        {intl.formatMessage({ defaultMessage: "Before you continue" })}
      </Typography>

      <Typography sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.lg })}>
        {intl.formatMessage({
          defaultMessage: "Please confirm the following before submitting your request:"
        })}
      </Typography>

      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          marginBlockEnd: theme.tokens.spacing.xl
        })}
      >
        <FormControlLabel
          required
          label={
            <FormattedMessage defaultMessage="Carrot can only authorize one transaction at a time. I won't be able to submit another request until I use my Carrot Card for this expense." />
          }
          control={
            <Checkbox
              checked={acknowledgements.oneTransactionAtATime}
              onChange={(e) => setAcknowledgements({ ...acknowledgements, oneTransactionAtATime: e.target.checked })}
            />
          }
        />

        <FormControlLabel
          required
          label={
            <FormattedMessage
              defaultMessage="I've checked my <link>benefit guide</link> and confirmed that my expense and provider are eligible. If I use my card for an ineligible expense, I'll need to pay Carrot back for that amount."
              values={{
                link: (linkContent) => (
                  <Link
                    component={RouterLink}
                    to={DbgPaths.CARROT_ELIGIBLE}
                    color="inherit"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {linkContent}
                  </Link>
                )
              }}
            />
          }
          control={
            <Checkbox
              checked={acknowledgements.checkedBenefitGuide}
              onChange={(e) => setAcknowledgements({ ...acknowledgements, checkedBenefitGuide: e.target.checked })}
            />
          }
        />

        <FormControlLabel
          required
          label={
            <FormattedMessage
              defaultMessage="I understand that I'll need to upload an itemized statement and any other <link>required documents</link> after I use my Carrot Card."
              values={{
                link: (linkContent) => (
                  <Link
                    component={RouterLink}
                    to={DbgPaths.REQUIRED_DOCS}
                    color="inherit"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {linkContent}
                  </Link>
                )
              }}
            />
          }
          control={
            <Checkbox
              checked={acknowledgements.uploadDocuments}
              onChange={(e) => setAcknowledgements({ ...acknowledgements, uploadDocuments: e.target.checked })}
            />
          }
        />

        <FormControlLabel
          required
          label={
            <FormattedMessage
              defaultMessage="I acknowledge that, depending on location and other factors, Carrot funds I use may be subject to <link>taxes</link>."
              values={{
                link: (linkContent) => (
                  <Link
                    component={RouterLink}
                    to={DbgPaths.TAXES}
                    color="inherit"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {linkContent}
                  </Link>
                )
              }}
            />
          }
          control={
            <Checkbox
              checked={acknowledgements.subjectToTaxes}
              onChange={(e) => setAcknowledgements({ ...acknowledgements, subjectToTaxes: e.target.checked })}
            />
          }
        />

        <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>
          <Typography>
            <FormattedMessage
              defaultMessage="If you have questions, <link>contact us</link>."
              values={{
                link: (linkContent) => (
                  <Link
                    component={RouterLink}
                    to="/care-team"
                    color="inherit"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Typography>
        </Box>
      </Box>

      <Box sx={(theme) => ({ display: "flex", justifyContent: "flex-end", marginBlockStart: theme.tokens.spacing.lg })}>
        <Button
          variant="outlined"
          color="secondary"
          disabled={!Object.values(acknowledgements).every((value) => value === true)}
          onClick={() => send("")}
        >
          <FormattedMessage defaultMessage="Continue" />
        </Button>
      </Box>
    </Box>
  )
}
